// src/utils/reports.js
//
// Relatórios impressos da Intranet, usando o layout padrão (printLayout.js).

import { openPrintDocument, escapeHtml, textToHtml, fmtBRL, fmtDate, fmtDateTime } from './printLayout';

// ---------------------------------------------------------------------------
// FINANCEIRO — Relatório de Cobranças
// ---------------------------------------------------------------------------
const ETAPA_STYLE = {
    a_faturar: { label: 'A Faturar', color: '#7c3aed' },
    a_cobrar:  { label: 'A Cobrar',  color: '#d97706' },
    cobrado:   { label: 'Cobrança Enviada', color: '#0891b2' },
    recebido:  { label: 'Recebido',  color: '#059669' },
    cancelado: { label: 'Cancelado', color: '#64748b' }
};
const TIPO_LABEL = { com_nota: 'Com NF', sem_nota: 'Sem NF', sem_cobranca: 'Contrato', a_faturar: 'A definir' };

/**
 * @param {Array} items           cobranças (já filtradas)
 * @param {object} opts
 * @param {string} opts.filtersText  descrição dos filtros aplicados
 * @param {string} opts.emittedBy
 */
export const printFinanceReport = (items, { filtersText = '', emittedBy = '' } = {}) => {
    const today = new Date().toISOString().slice(0, 10);

    // Resumo por etapa
    const summary = {};
    Object.keys(ETAPA_STYLE).forEach(k => { summary[k] = { count: 0, sum: 0 }; });
    items.forEach(c => {
        const e = summary[c.etapa] ? c.etapa : 'a_cobrar';
        summary[e].count++; summary[e].sum += Number(c.valor) || 0;
    });
    const cardsHtml = Object.entries(ETAPA_STYLE).map(([k, s]) => `
        <div class="card" style="--c:${s.color}">
            <span class="label">${s.label}</span>
            <div class="big mono">${fmtBRL(summary[k].sum)}</div>
            <div class="small">${summary[k].count} cobrança(s)</div>
        </div>`).join('');

    // Agrupa por empresa
    const groups = new Map();
    items.forEach(c => {
        const key = c.empresaNome || 'Sem empresa';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(c);
    });
    const sortedGroups = [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));

    let rows = '';
    sortedGroups.forEach(([empresa, list]) => {
        const sorted = [...list].sort((a, b) => (a.numeroSequencial || 0) - (b.numeroSequencial || 0));
        const subtotal = sorted.reduce((acc, c) => acc + (Number(c.valor) || 0), 0);
        const razao = sorted[0]?.empresaRazaoSocial && sorted[0].empresaRazaoSocial !== empresa ? ` <span style="font-weight:600;color:#64748b">· ${escapeHtml(sorted[0].empresaRazaoSocial)}</span>` : '';
        rows += `<tr class="group-row"><td colspan="7">${escapeHtml(empresa)}${razao}</td></tr>`;
        sorted.forEach(c => {
            const st = ETAPA_STYLE[c.etapa] || ETAPA_STYLE.a_cobrar;
            const vhsys = (c.vhsysRpsNumero || c.vhsysNfseId) ? `NFS-e RPS ${c.vhsysRpsNumero || c.vhsysNfseId}` : (c.vhsysReceitaId ? `Receita ${c.vhsysReceitaId}` : '<span style="color:#d97706">Não lançado</span>');
            const overdue = ['a_cobrar', 'cobrado'].includes(c.etapa || 'a_cobrar') && c.vencimento && c.vencimento < today;
            const venc = c.vencimento ? c.vencimento.split('-').reverse().join('/') : '—';
            rows += `
                <tr>
                    <td class="mono" style="font-weight:700;color:#0891b2">A${escapeHtml(c.numeroSequencial)}</td>
                    <td>${fmtDate(c.dataChamado)}</td>
                    <td>${escapeHtml(c.titulo)}${c.observacoes ? `<div class="muted" style="font-size:8pt;margin-top:2px">${escapeHtml(c.observacoes)}</div>` : ''}</td>
                    <td>${TIPO_LABEL[c.faturamentoTipo] || '—'}<div class="muted" style="font-size:7.5pt">${vhsys}</div></td>
                    <td class="center" style="${overdue ? 'color:#dc2626;font-weight:700' : ''}">${venc}</td>
                    <td class="center"><span class="pill" style="color:${st.color}">${st.label}</span></td>
                    <td class="right mono" style="font-weight:700">${fmtBRL(c.valor)}</td>
                </tr>`;
        });
        rows += `<tr class="subtotal-row"><td colspan="6" class="right">Subtotal ${escapeHtml(empresa)} (${sorted.length})</td><td class="right mono">${fmtBRL(subtotal)}</td></tr>`;
    });

    const total = items.reduce((acc, c) => acc + (Number(c.valor) || 0), 0);

    const bodyHtml = `
        ${filtersText ? `<div class="box" style="font-size:9pt"><span class="label">Filtros aplicados</span>${escapeHtml(filtersText)}</div>` : ''}
        <div class="section">
            <h3 class="section-title">Resumo</h3>
            <div class="cards" style="grid-template-columns:repeat(5,1fr)">${cardsHtml}</div>
        </div>
        <div class="section" style="break-inside:auto">
            <h3 class="section-title">Cobranças por empresa</h3>
            ${items.length === 0 ? '<div class="box muted center">Nenhuma cobrança no filtro selecionado.</div>' : `
            <table>
                <thead><tr>
                    <th style="width:52px">Nº</th><th style="width:70px">Data</th><th>Descrição</th><th style="width:110px">Tipo / VHSYS</th>
                    <th style="width:66px" class="center">Venc.</th><th style="width:78px" class="center">Etapa</th><th style="width:88px" class="right">Valor</th>
                </tr></thead>
                <tbody>${rows}</tbody>
            </table>
            <div class="totals">
                <div class="row"><span>Cobranças</span><span>${items.length}</span></div>
                <div class="row"><span>Empresas</span><span>${sortedGroups.length}</span></div>
                <div class="row grand"><span>Total</span><span class="mono">${fmtBRL(total)}</span></div>
            </div>`}
        </div>`;

    return openPrintDocument({
        docTitle: `Relatório de Cobranças - ${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}`,
        kicker: 'Financeiro',
        title: 'Relatório de Cobranças',
        subtitle: `${items.length} cobrança(s) · ${fmtBRL(total)}`,
        bodyHtml,
        footerNote: 'Relatório financeiro — uso interno',
        emittedBy
    });
};

// ---------------------------------------------------------------------------
// CHAMADO — Relatório de Atendimento
// ---------------------------------------------------------------------------
const PRIORITY_LABEL = { low: 'Baixa', medium: 'Média', high: 'Alta', urgent: 'Urgente', baixa: 'Baixa', media: 'Média', alta: 'Alta' };

/**
 * @param {object} ticket
 * @param {object} opts
 * @param {Array}  opts.comments          comentários do chamado
 * @param {boolean} opts.includeInternal  incluir comentários internos
 * @param {string} opts.companyName       nome de exibição da empresa
 * @param {string} opts.statusColor       cor do status
 * @param {Array}  opts.templates         categorias técnicas adicionadas ao chamado [{id,name,fields}]
 * @param {Array}  opts.users             lista de usuários (para nomes dos técnicos)
 * @param {string} opts.emittedBy
 */
export const printTicketReport = (ticket, { comments = [], includeInternal = true, companyName = '', statusColor = '#0891b2', templates = [], users = [], emittedBy = '' } = {}) => {
    const t = ticket || {};
    const techs = (t.assignedToEmail || []).map(email => {
        const u = users.find(x => x.email === email);
        return u?.displayName || u?.name || email.split('@')[0];
    });
    const sched = t.isScheduled && t.scheduledDate
        ? `${t.scheduledDate.split('-').reverse().join('/')}${t.scheduledTime ? ` às ${t.scheduledTime}` : ''} (${t.scheduledType || 'Remoto'})`
        : '';

    const cell = (label, value, wide = false) => `<div class="cell${wide ? ' wide' : ''}"><span class="label">${label}</span><span class="value">${value || '<span class="muted">—</span>'}</span></div>`;
    const infoHtml = `
        <div class="info-grid">
            ${cell('Empresa', escapeHtml(companyName))}
            ${cell('Solicitante', escapeHtml(t.clientName))}
            ${cell('Protocolo', escapeHtml(t.protocolo))}
            ${cell('E-mail', escapeHtml(t.clientEmail))}
            ${cell('WhatsApp', escapeHtml(t.clientWhatsApp))}
            ${cell('Prioridade', escapeHtml(PRIORITY_LABEL[t.priority] || t.priority))}
            ${cell('Abertura', fmtDateTime(t.createdAt))}
            ${cell('Técnico(s)', escapeHtml(techs.join(', ')))}
            ${cell('Agendamento', escapeHtml(sched))}
            ${t.contractName ? cell('Contrato', escapeHtml(t.contractName), true) : ''}
        </div>`;

    // Informações técnicas
    const techData = t.technicalData || {};
    const techHtml = templates.map(tpl => {
        const fields = (tpl.fields || []).map(f => {
            const v = techData?.[tpl.id]?.[f.name];
            return `<div class="cell"><span class="label">${escapeHtml(f.name)}</span><span class="value">${v ? textToHtml(v) : '<span class="muted">—</span>'}</span></div>`;
        }).join('');
        return `
            <div style="margin-bottom:10px;break-inside:avoid">
                <div style="font-size:8.5pt;font-weight:800;color:#0e7490;margin-bottom:4px">${escapeHtml(tpl.name)}</div>
                <div class="info-grid">${fields || '<div class="cell wide muted">Sem campos.</div>'}</div>
            </div>`;
    }).join('');

    // Serviços
    const services = t.services || [];
    const subtotal = services.reduce((acc, s) => acc + (Number(s.total) || 0), 0);
    const discount = Number(t.discountPercent) || 0;
    const servicesHtml = services.length ? `
        <table>
            <thead><tr><th>Serviço</th><th class="right" style="width:100px">Unitário</th><th class="center" style="width:60px">Qtd.</th><th class="right" style="width:100px">Total</th></tr></thead>
            <tbody>${services.map(s => `
                <tr>
                    <td style="font-weight:600">${escapeHtml(s.name || s.descricao)}</td>
                    <td class="right mono">${fmtBRL(s.value)}</td>
                    <td class="center mono">${escapeHtml(s.quantity ?? 1)}</td>
                    <td class="right mono" style="font-weight:700">${fmtBRL(s.total)}</td>
                </tr>`).join('')}
            </tbody>
        </table>
        <div class="totals">
            ${discount > 0 ? `<div class="row"><span>Subtotal</span><span class="mono">${fmtBRL(subtotal)}</span></div>
            <div class="row"><span>Desconto</span><span class="mono">${discount}%</span></div>` : ''}
            <div class="row grand"><span>Total</span><span class="mono">${fmtBRL(t.totalAmount ?? subtotal)}</span></div>
        </div>` : '';

    // Comentários (ordem cronológica)
    const visibleComments = [...comments]
        .filter(c => includeInternal || !c.isInternal)
        .sort((a, b) => {
            const da = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
            const dbb = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
            return da - dbb;
        });
    const commentsHtml = visibleComments.map((c, idx) => `${idx === 0 ? '<div style="break-inside:avoid"><h3 class="section-title">Histórico do atendimento</h3><div class="timeline">' : ''}
        <div class="tl-item${c.isInternal ? ' internal' : ''}">
            <div class="tl-head">
                <span><b>${escapeHtml(c.userName || (c.userEmail ? c.userEmail.split('@')[0] : 'Sistema Arcus'))}</b>${c.isInternal ? '<span class="tag-internal">INTERNO</span>' : ''}</span>
                <span>${fmtDateTime(c.createdAt)}</span>
            </div>
            <div class="tl-body">${textToHtml(c.text)}</div>
        </div>${idx === 0 ? '</div></div><div class="timeline" style="margin-top:0;padding-top:0">' : ''}`).join('') + (visibleComments.length ? '</div>' : '');

    const bodyHtml = `
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:16px">
            <div>
                <div class="mono" style="font-size:9pt;font-weight:800;color:#0891b2">CHAMADO A${escapeHtml(t.numeroSequencial)}</div>
                <div style="font-size:14pt;font-weight:800;color:#0f172a;line-height:1.25">${escapeHtml(t.title)}</div>
            </div>
            <span class="pill" style="color:${escapeHtml(statusColor)};margin-top:4px">${escapeHtml(t.status)}</span>
        </div>

        <div class="section">
            <h3 class="section-title">Dados do atendimento</h3>
            ${infoHtml}
        </div>

        <div class="section">
            <h3 class="section-title">Solicitação</h3>
            <div class="box">${textToHtml(t.description) || '<span class="muted">Sem descrição.</span>'}</div>
        </div>

        ${techHtml ? `<div class="section"><h3 class="section-title">Informações técnicas</h3>${techHtml}</div>` : ''}

        ${servicesHtml ? `<div class="section"><h3 class="section-title">Serviços executados</h3>${servicesHtml}</div>` : ''}

        ${t.closeNotes ? `<div class="section"><h3 class="section-title">Observações de encerramento</h3><div class="box">${textToHtml(t.closeNotes)}</div></div>` : ''}

        <div class="section" style="break-inside:auto">
            ${visibleComments.length ? commentsHtml : '<h3 class="section-title">Histórico do atendimento</h3><div class="box muted center">Nenhum registro.</div>'}
        </div>

        <div class="signatures">
            <div><b>${escapeHtml(techs[0] || 'Arcus Informática')}</b>Técnico responsável</div>
            <div><b>${escapeHtml(t.clientName || companyName || 'Cliente')}</b>Cliente</div>
        </div>`;

    return openPrintDocument({
        docTitle: `Chamado A${t.numeroSequencial} - ${t.title || ''}`.slice(0, 120),
        kicker: 'Relatório de Atendimento',
        title: `Chamado A${t.numeroSequencial}`,
        subtitle: companyName,
        bodyHtml,
        footerNote: includeInternal ? 'Relatório de atendimento — inclui registros internos' : 'Relatório de atendimento',
        emittedBy
    });
};

// ---------------------------------------------------------------------------
// CHAMADOS FINALIZADOS — Relatório de período
// ---------------------------------------------------------------------------
/**
 * @param {Array} tickets          chamados (já filtrados)
 * @param {object} opts
 * @param {Function} opts.getCompanyName  (ticket) => nome de exibição da empresa
 * @param {Function} opts.getStatusColor  (status) => cor
 * @param {string} opts.periodText        descrição do período
 * @param {string} opts.filtersText       descrição dos demais filtros
 * @param {string} opts.emittedBy
 */
export const printFinishedTicketsReport = (tickets, { getCompanyName = () => '', getStatusColor = () => '#0891b2', periodText = '', filtersText = '', emittedBy = '' } = {}) => {
    const sorted = [...tickets].sort((a, b) => (b.numeroSequencial || 0) - (a.numeroSequencial || 0));
    const total = sorted.reduce((acc, t) => acc + (Number(t.totalAmount) || 0), 0);
    const withValue = sorted.filter(t => (Number(t.totalAmount) || 0) > 0).length;

    // Resumo por status
    const byStatus = new Map();
    sorted.forEach(t => {
        const k = t.status || 'Sem status';
        const e = byStatus.get(k) || { count: 0, sum: 0 };
        e.count++; e.sum += Number(t.totalAmount) || 0;
        byStatus.set(k, e);
    });
    const statusRows = [...byStatus.entries()].sort((a, b) => b[1].count - a[1].count).map(([st, e]) => `
        <tr>
            <td><span class="pill" style="color:${escapeHtml(getStatusColor(st))}">${escapeHtml(st)}</span></td>
            <td class="center mono">${e.count}</td>
            <td class="right mono">${((e.count / (sorted.length || 1)) * 100).toFixed(1)}%</td>
            <td class="right mono" style="font-weight:700">${fmtBRL(e.sum)}</td>
        </tr>`).join('');

    const rows = sorted.map(t => `
        <tr>
            <td class="mono" style="font-weight:700;color:#0891b2">A${escapeHtml(t.numeroSequencial)}</td>
            <td>${fmtDate(t.createdAt)}</td>
            <td>${escapeHtml(t.title)}<div class="muted" style="font-size:8pt">${escapeHtml(t.clientName || '')}</div></td>
            <td>${escapeHtml(getCompanyName(t) || '—')}</td>
            <td style="font-size:8pt;font-weight:700;color:${escapeHtml(getStatusColor(t.status))}">${escapeHtml(t.status)}</td>
            <td class="right mono" style="font-weight:700">${(Number(t.totalAmount) || 0) > 0 ? fmtBRL(t.totalAmount) : '<span class="muted">—</span>'}</td>
        </tr>`).join('');

    const bodyHtml = `
        ${(periodText || filtersText) ? `<div class="box" style="font-size:9pt"><span class="label">Filtros aplicados</span>${escapeHtml([periodText && `Período: ${periodText}`, filtersText].filter(Boolean).join(' · '))}</div>` : ''}
        <div class="section">
            <h3 class="section-title">Resumo</h3>
            <div class="cards" style="grid-template-columns:repeat(3,1fr)">
                <div class="card" style="--c:#0891b2"><span class="label">Chamados</span><div class="big mono">${sorted.length}</div><div class="small">finalizados no filtro</div></div>
                <div class="card" style="--c:#d97706"><span class="label">Com valor</span><div class="big mono">${withValue}</div><div class="small">chamados com serviços cobrados</div></div>
                <div class="card" style="--c:#059669"><span class="label">Valor total</span><div class="big mono">${fmtBRL(total)}</div><div class="small">soma dos chamados</div></div>
            </div>
        </div>
        ${sorted.length ? `
        <div class="section">
            <h3 class="section-title">Por status</h3>
            <table>
                <thead><tr><th>Status</th><th class="center" style="width:80px">Qtd.</th><th class="right" style="width:80px">%</th><th class="right" style="width:110px">Valor</th></tr></thead>
                <tbody>${statusRows}</tbody>
            </table>
        </div>` : ''}
        <div class="section" style="break-inside:auto">
            <h3 class="section-title">Chamados</h3>
            ${sorted.length === 0 ? '<div class="box muted center">Nenhum chamado no filtro selecionado.</div>' : `
            <table>
                <thead><tr>
                    <th style="width:52px">Nº</th><th style="width:70px">Data</th><th>Título / Solicitante</th><th style="width:110px">Empresa</th><th style="width:90px">Status</th><th class="right" style="width:85px">Valor</th>
                </tr></thead>
                <tbody>${rows}</tbody>
            </table>
            <div class="totals">
                <div class="row"><span>Chamados</span><span>${sorted.length}</span></div>
                <div class="row grand"><span>Total</span><span class="mono">${fmtBRL(total)}</span></div>
            </div>`}
        </div>`;

    return openPrintDocument({
        docTitle: `Chamados Finalizados${periodText ? ` - ${periodText}` : ''}`.replace(/\//g, '-'),
        kicker: 'Chamados',
        title: 'Chamados Finalizados',
        subtitle: `${periodText ? `${periodText} · ` : ''}${sorted.length} chamado(s) · ${fmtBRL(total)}`,
        bodyHtml,
        footerNote: 'Relatório de chamados finalizados — uso interno',
        emittedBy
    });
};
